<template>
    <mode-switcher
        ref="modeSwitcher"
        :main-mode-text="$t('create')"
        :create-mode-text="$t('surcharge_creating')"
        :edit-mode-text="$t('surcharge_editing')"
        @reset="surchargeEditModalSelected = null"
    >
        <template v-slot:btn-block>
            <div class="full-width d-flex align-center">
                <v-spacer />
                <FilterStatusToggle v-model="activeFilter" />
            </div>
        </template>
        <template v-slot:main-mode>
            <v-data-table
                :items="surchargesFiltered"
                :headers="headers"
                :loading="isSurchargesLoading"
                class="elevation-1 table-server"
                :sort-by="[{key: 'name', order: 'asc'}]"
                must-sort
            >
                <template v-slot:item.name="{item}">
                    <span class="text-link font-weight-medium text-decoration-underline" @click="showEditModal(item)">
                        {{ item.name }}
                    </span>
                </template>
                <template v-slot:item.dates="{item}">
                    {{ dateRange(item.tariffLinks) }}
                </template>
                <template v-slot:item.active="{item}">
                    <span>
                        {{ item.active ? $t('active') : $t('inactive') }}
                    </span>
                </template>
                <template v-slot:item.actions="{item}">
                    <span>
                        <v-btn variant="text" size="small" color="primary" @click="copySurcharge(item)">
                            {{ $t('copy') }}
                        </v-btn>
                    </span>
                </template>
            </v-data-table>
        </template>
        <template v-slot:additional-mode>
            <product-prices-surcharges-modal
                :surcharge="surchargeEditModalSelected"
                :contract="contract"
                :selected-locale="selectedLocale"
                @on-close="onCloseEditModal"
                @on-save="onSurchargeSave"
            />
        </template>
    </mode-switcher>
</template>

<script lang="ts">
    import {Component, Emit, Prop, Vue} from 'vue-facing-decorator'
    import ProductPricesSurchargesModal from '~/components/supplier/prices/modals/ProductPricesSurchargesModal.vue'
    import {clone} from '@/utils/helpers'

    @Component({
        components: {
            ProductPricesSurchargesModal,
        },
    })
    export class ProductPricesSurcharges extends Vue {
        @Prop() contract
        @Prop() selectedLocale
        @Prop() surcharges
        @Prop() isSurchargesLoading

        surchargeEditModalSelected = null
        activeFilter = true

        @Emit()
        async onSurchargeSave() {}

        showEditModal(surcharge) {
            this.surchargeEditModalSelected = surcharge
            this.$refs.modeSwitcher.edit()
        }

        onCloseEditModal() {
            this.$refs.modeSwitcher.goToTable()
        }

        copySurcharge(surcharge) {
            const surchargeCopied = clone(surcharge)

            delete surchargeCopied.id

            surchargeCopied.tariffLinks?.forEach(tl => {
                delete tl.linkId
            })
            surchargeCopied.accommodationSurcharges?.forEach(s => {
                delete s.id
            })
            surchargeCopied.lengthOfStaySurcharges?.forEach(s => {
                delete s.id
            })
            surchargeCopied.prices?.forEach(p => {
                delete p.id
            })

            this.showEditModal(surchargeCopied)
        }

        get headers() {
            return [
                {title: this.$t('name'), value: 'name', sortable: true},
                {title: this.$t('dates'), value: 'dates'},
                {title: this.$t('status'), value: 'active'},
                {title: this.$t('actions'), value: 'actions'},
            ]
        }

        get surchargesFiltered() {
            return (this.surcharges || []).filter(el => this.activeFilter === el.active)
        }

        dateRange(tariffs) {
            if (!tariffs?.length) return ''
            let earliestDateFrom = tariffs[0].dateFrom
            let latestDateTo = tariffs[0].dateTo

            tariffs.forEach(tariff => {
                if (tariff.dateFrom && new Date(tariff.dateFrom) < new Date(earliestDateFrom)) {
                    earliestDateFrom = tariff.dateFrom
                }
                if (tariff.dateTo && new Date(tariff.dateTo) > new Date(latestDateTo)) {
                    latestDateTo = tariff.dateTo
                }
            })

            return `${earliestDateFrom && this.$filters.dateShortFormat(earliestDateFrom)} - ${latestDateTo && this.$filters.dateShortFormat(latestDateTo)}`
        }
    }

    export default ProductPricesSurcharges
</script>
